import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tab, Tabs, ListGroup, Alert } from 'react-bootstrap';
import './SearchBar.css';
import { getItemGroupSearch } from "../../api";
import debounce from 'lodash/debounce';

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.361 18.2168L14.601 13.2662C15.8249 11.8113 16.4954 9.98069 16.4954 8.07499C16.4954 3.62251 12.8729 0 8.42045 0C3.96797 0 0.345459 3.62251 0.345459 8.07499C0.345459 12.5275 3.96797 16.15 8.42045 16.15C10.092 16.15 11.6849 15.6458 13.0467 14.6888L17.8429 19.677C18.0434 19.8852 18.3113 20 18.602 20C18.8798 20 19.1397 19.8957 19.337 19.7061C19.7506 19.3129 19.7668 18.6303 19.361 18.2168ZM8.42045 2.10652C11.7115 2.10652 14.3889 4.78391 14.3889 8.07499C14.3889 11.3661 11.7115 14.0435 8.42045 14.0435C5.12937 14.0435 2.45198 11.3661 2.45198 8.07499C2.45198 4.78391 5.12937 2.10652 8.42045 2.10652Z" fill="#D1181E"/>
  </svg>
);

const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [titleData, setTitleData] = useState([]);
  const [writerData, setWriterData] = useState([]);
  const [contentData, setContentData] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.trim().length >= 2) {
        setIsLoading(true);
        try {
          const result = await getItemGroupSearch(query);
          if (result.data) {
            const { titleData, writerData, contentData } = result.data;
            setTitleData(titleData || []);
            setWriterData(writerData || []);
            setContentData(contentData || []);
            setShowResults(true);
          }
        } catch (error) {
          console.error('Search error:', error);
        }
        setIsLoading(false);
      } else {
        clearData();
      }
    }, 300),
    []
  );
  
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    setSearchQuery('');
    setShowResults(false);
  }, [location.pathname]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const clearData = () => {
    setTitleData([]);
    setContentData([]);
    setWriterData([]);
    setShowResults(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    clearData();
  };

  return (
    <div className="search-container" onBlur={(e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setTimeout(clearData, 200);
      }
    }}>
      <div className="search-wrapper">
        <input
          type="text"
          className="search-input"
          placeholder={showPlaceholder ? "Detaylı Arama" : ""}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            setShowPlaceholder(false);
            if (searchQuery.trim().length >= 2) {
              setShowResults(true);
            }
          }}
          onBlur={() => {
            if (!searchQuery) {
              setShowPlaceholder(true);
            }
          }}
        />
        <span className="search-button" disabled={isLoading}>
          {isLoading ? (
            <div className="search-spinner"></div>
          ) : (
            <SearchIcon />
          )}
        </span>
      </div>

      {showResults && searchQuery.trim().length >= 2 && (
        <div className="search-results">
          {(titleData.length > 0 || contentData.length > 0 || writerData.length > 0) ? (
            <Tabs 
              defaultActiveKey={titleData.length > 0 ? "tab1" : contentData.length > 0 ? "tab2" : "tab3"}
              className="search-tabs"
            >
              {titleData.length > 0 && (
                <Tab eventKey="tab1" title={`Başlık (${titleData.length})`}>
                  <ListGroup>
                    {titleData.map((item, index) => (
                      <ListGroup.Item 
                        action 
                        key={`title-${index}`}
                        onClick={() => handleNavigate(`/article/${item.id}`)}
                      >
                        <strong className="d-block">{item.title}</strong>
                        {item.definition && <p>{item.definition}</p>}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Tab>
              )}

              {contentData.length > 0 && (
                <Tab eventKey="tab2" title={`İçerik (${contentData.length})`}>
                  <ListGroup>
                    {contentData.map((item, index) => (
                      <ListGroup.Item 
                        action 
                        key={`content-${index}`}
                        onClick={() => handleNavigate(`/article/${item.id}`)}
                      >
                        <strong className="d-block">{item.title}</strong>
                        {item.definition && <p>{item.definition}</p>}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Tab>
              )}

              {writerData.length > 0 && (
                <Tab eventKey="tab3" title={`Yazar (${writerData.length})`}>
                  <ListGroup>
                    {writerData.map((item, index) => (
                      <ListGroup.Item 
                        action 
                        key={`writer-${index}`}
                        onClick={() => handleNavigate(`/yazar/${item.userId}`)}
                      >
                        <strong className="d-block">{item.writerName}  </strong>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Tab>
              )}
            </Tabs>
          ) : (
            <Alert variant="info" className="m-3">
              {isLoading ? 'Aranıyor...' : 'Sonuç bulunamadı'}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar; 